import Vue from 'vue';
import api from 'src/api';
import {
  notice, commandType, auth, notice as channels, l,
} from 'src/utils';
import { isEmpty } from 'lodash';
import secondsToHms from 'src/filters/secondsToHms';
import toast from 'src/utils/toast';
import i18n from 'src/features/i18n';

// в константы выносим только названия мутаций,
// которые переиспользуем/планируем переиспользовать
export const SET_LOADING_REPORT = 'SET_LOADING_REPORT';
export const SET_STOP_LOADING_REPORT = 'SET_STOP_LOADING_REPORT';

const data = {
  operators: [],
  totalCount: 0,
  statistics: {},
  activitiesDetailReport: undefined,
  activitiesQueueReport: undefined,
  activitiesEmploymentReport: undefined,
  operationalReports: {
    activities: {
      total: {
        closedByClient: undefined,
        closedByOperator: undefined,
        closedByAuto: undefined,
        pending: undefined,
        abandoned: undefined,
      },
      byGroup: [],
    },
    operators: {
      signedUpOperators: undefined,
      readyOperators: undefined,
      notReadyOperators: undefined,
      avatarUrl: null,
    },
  },
  maximumSkillValue: 0,
  activitiesDetailReportDates: {
    dateFrom: '',
    dateTo: '',
    page: 1,
    totalCount: 0,
    pageSize: undefined,
  },
  activitiesQueueReportDates: {
    dateFrom: '',
    dateTo: '',
  },
  activitiesEmploymentReportDates: {
    dateFrom: '',
    dateTo: '',
    page: 1,
    totalCount: 0,
    pageSize: undefined,
  },
  activitiesStates: [],
  activityQueue: [],
  loadingReport: false,
  csvReports: {
    queueReport: {},
    employmentReport: {},
    activityReport: {},
  },
  teamId: null,
  detailReportEnquiredWithoutExport: false,
  queueReportEnquiredWithoutExport: false,
  employmentReportEnquiredWithoutExport: false,
};

function updateOperators(commit, params) {
  commit('OPERATORS', params);
}

function updateStatistics(commit, params) {
  commit('STATISTICS', params);
}

function updateOperationalReports(commit, params) {
  commit('OPERATIONAL_REPORTS', params);
}

function changeOperatorStatus(params) {
  const { idCurrent } = params;
  delete params.idCurrent;
  return notice.push(idCurrent, commandType.CHANGE_OPERATOR_STATUS, params);
}

function updateActivityQueue(commit, payload) {
  commit('SET_ACTIVITY_QUEUE', payload.data);
}

function convertSnakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function onCSVReportFailed(commit, {
  teamId,
  reportType,
  csvReports,
}) {
  const camelReportType = convertSnakeToCamel(reportType);
  const currentTypeReports = csvReports[camelReportType];
  const currentReport = currentTypeReports ? currentTypeReports[teamId] : null;
  const prevReportUrl = currentReport?.url || null;
  commit('SET_CSV_REPORT_STATUS', {
    reportType,
    teamId,
    status: prevReportUrl ? 'completed' : 'notStarted',
  });
  toast.error({
    msg: i18n.t('T_ERROR_EXPORT'),
    duration: 10000,
  });
}

function onCSVReportCancelled(commit, {
  teamId,
  reportType,
  csvReports,
}) {
  const camelReportType = convertSnakeToCamel(reportType);
  const currentTypeReports = csvReports[camelReportType];
  const currentReport = currentTypeReports ? currentTypeReports[teamId] : null;
  const prevReportUrl = currentReport?.url || null;
  commit('SET_CSV_REPORT_STATUS', {
    reportType,
    teamId,
    status: prevReportUrl ? 'completed' : 'notStarted',
  });
  toast.info({
    msg: i18n.t('T_EXPORT_OF_CSV_CANCELLED'),
    duration: 10000,
  });
}

const actions = {
  updateOperatorStatus({ commit, rootState }, { id, ready }) {
    const result = changeOperatorStatus({
      idCurrent: rootState.home.currentTeam,
      id,
      ready,
    }); // TODO need promise send
    commit('CHANGE_READY', { id, ready });
    return result;
  },
  update({ commit, state, rootState }, event) {
    switch (event.type) {
      case commandType.OPERATORS:
        updateOperators(commit, event.data);
        break;
      case commandType.STATISTICS:
        updateStatistics(commit, event.data);
        break;
      case commandType.OPERATIONAL_REPORTS_UPDATE:
        updateOperationalReports(commit, event.data);
        break;
      case commandType.JOIN:
        break;
      case commandType.CSV_REPORT_FINISHED:
        commit('SET_CSV_REPORT_DATA', {
          reportType: event.data.reportType,
          teamId: rootState.home.currentTeam,
          status: 'completed',
          url: event.data.url,
        });
        break;
      case commandType.CSV_REPORT_IN_PROGRESS:
        commit('SET_CSV_REPORT_STATUS', {
          reportType: event.data.reportType,
          teamId: rootState.home.currentTeam,
          status: 'inProgress',
        });
        break;
      case commandType.CSV_REPORT_FAILED:
        onCSVReportFailed(commit, {
          reportType: event.data.reportType,
          teamId: rootState.home.currentTeam,
          csvReports: state.csvReports,
        });
        break;
      case commandType.CSV_REPORT_CANCELLED:
        onCSVReportCancelled(commit, {
          reportType: event.data.reportType,
          teamId: rootState.home.currentTeam,
          csvReports: state.csvReports,
        });
        break;
      case commandType.ACTIVITY_QUEUE:
        updateActivityQueue(commit, event.data);
        break;
      default:
        break;
    }
  },
  async fetchActivitiesDetailReport({ commit }, {
    dateFrom, dateTo, page, pageSize, moreData = false,
  }) {
    commit(SET_LOADING_REPORT);
    const newPage = moreData ? page + 1 : page;
    api.getDetailReport({
      dateFrom, dateTo, page: newPage, pageSize,
    }).then(({ activityDetailReportRows, totalCount, pageSize: pageSizeDefault }) => {
      commit('SET_ACTIVITIES_DETAIL_REPORT', { activitiesReport: activityDetailReportRows, moreData });
      commit('SET_ACTIVITIES_REPORT_DATES', {
        dateFrom, dateTo, page: newPage, totalCount, pageSize: pageSizeDefault,
      });
      commit(SET_STOP_LOADING_REPORT);
    })
      .catch(() => {
        commit(SET_STOP_LOADING_REPORT);
      });
  },
  fetchActivitiesQueueReport({ commit }, {
    dateFrom, dateTo, channelIds, requestTypeIds,
  }) {
    commit(SET_LOADING_REPORT);
    api.getQueryReport({
      dateFrom, dateTo, channelIds, requestTypeIds,
    }).then((queueReportRows) => {
      commit('SET_ACTIVITIES_QUEUE_REPORT', { activitiesReport: queueReportRows });
      commit('SET_ACTIVITIES_QUEUE_DATES', { dateFrom, dateTo });
      commit(SET_STOP_LOADING_REPORT);
    })
      .catch(() => {
        commit(SET_STOP_LOADING_REPORT);
      });
  },
  async fetchActivitiesEmploymentReport({ commit }, {
    dateFrom, dateTo, page, pageSize, moreData = false,
  }) {
    commit(SET_LOADING_REPORT);
    const newPage = moreData ? page + 1 : page;
    api.getEmploymentReport({
      dateFrom, dateTo, page: newPage, pageSize,
    }).then(({
      absenceReasons, operators, totalCount, pageSize: pageSizeDefault,
    }) => {
      commit('SET_ACTIVITIES_EMPLOYMENT_REPORT', { activitiesReport: { absenceReasons, operators }, moreData });
      commit('SET_ACTIVITIES_EMPLOYMENT_DATES', {
        dateFrom, dateTo, page: newPage, totalCount, pageSize: pageSizeDefault,
      });
      commit(SET_STOP_LOADING_REPORT);
    })
      .catch(() => {
        commit(SET_STOP_LOADING_REPORT);
      });
  },
  skills(_, { operatorId }) { // TODO проверить необходимость существования - выпилить?
    return api.getSkills(operatorId).then((res) => res);
  },
  getOperatorSettings(_, { operatorId }) {
    return api.getOperatorSettings(operatorId).then((res) => res);
  },
  setOperatorSettings(_, params) {
    return api.setOperatorSettings(params).then((res) => res);
  },
  async loadMaximumSkillValue({ commit }) {
    const maximumValue = await api.getMaxSkillValue();
    commit('SET_MAXIMUM_SKILL_VALUE', maximumValue);
  },
  updateOperatorSkills(_, updateSkills) { // TODO проверить, вероятно выпилить
    // вероятно заменен setOperatorSettings
    return api.updateOperatorsSkills(updateSkills).then((res) => res);
  },
  async getActivitiesStates({ commit }) {
    const result = await api.getActivitiesStates();
    commit('SET_ACTIVITIES_STATES', result);
  },
  cancelCSVReport(_, msg) {
    return channels.push(msg.overseerId, commandType.CANCEL_CSV_REPORT, msg);
  },
  async getXMLHttpRequest({ commit }, { method, url }) {
    commit(SET_LOADING_REPORT);
    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${auth.getCurrentToken()}`,
        },
      });
      const contentType = response.headers.get('Content-Type');
      const responseData = contentType?.includes('application/json')
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        const error = new Error(`HTTP Error: ${response.status}`);
        error.response = {
          status: response.status,
          responseData,
        };
        throw error;
      }

      commit(SET_STOP_LOADING_REPORT);
      return responseData;
    } catch (error) {
      commit(SET_STOP_LOADING_REPORT);
      if (error.response) {
        throw error;
      }
      throw new Error(`Request failed: ${error.message}`);
    }
  },
  async fetchCsvReports({ commit }, { teamIds, overseerId }) {
    commit(SET_LOADING_REPORT);
    try {
      const csvReportsData = await api.getCsvReports({ teamIds, overseerId });
      csvReportsData.forEach((team) => {
        team.reports.forEach((report) => {
          commit('SET_CSV_REPORT_DATA', {
            reportType: report.reportType,
            teamId: team.teamId,
            status: report.url ? 'completed' : 'notStarted', // вероятно здесь будет только статус completed,
            // тк бэк присылает только данные об успешных отчетах
            url: report.url,
          });
        });
      });
    } catch (error) {
      l.error(error);
    } finally {
      commit(SET_STOP_LOADING_REPORT);
    }
  },
  async onStartExportError({ rootState }, { error }) {
    const teams = rootState.user.overseerTeams;
    const errorData = error.response.responseData;
    if (errorData.message.includes('csv already started')) {
      const reportTypeMapping = {
        queue_report: i18n.t('T_QUEUE_REPORT'),
        employment_report: i18n.t('T_REPORT_ON_EMPLOYMENT_OF_OPERATORS'),
        activity_report: i18n.t('T_DETAIL_REPORT'),
      };
      const readableReportType = reportTypeMapping[errorData.report.report_type] || i18n.t('T_UNKNOWN_REPORT_TYPE');
      const team = teams.find((t) => Number(t.id) === errorData.report.team_id);
      const teamName = team ? team.name : i18n.t('T_UNKNOWN_TEAM');
      const message = i18n.t('T_WAIT_EXPORT', {
        reportType: readableReportType,
        teamName,
      });
      toast.warning({
        msg: message,
        duration: 10000,
      });
    } else {
      toast.error({
        msg: i18n.t('T_ERROR_EXPORT'),
        duration: 10000,
      });
    }
  },
};

const mutations = {
  OPERATORS(state, { operators, totalCount }) {
    state.operators = !isEmpty(operators)
      ? operators.map((item) => ({
        ...item,
        art: secondsToHms(item.art),
        a1rt: secondsToHms(item.a1rt),
      }))
      : operators;
    state.totalCount = totalCount;
  },
  STATISTICS(state, statistics) {
    if (statistics) {
      state.statistics = statistics;
    }
  },
  RESTORE_READY(state, { index, ready }) {
    state.operators[index].status.ready = ready;
  },
  CHANGE_READY(state, { id, ready }) {
    state.operators.map((operator) => {
      if (operator.id === id) {
        operator.ready = ready;
      }
      return operator;
    });
  },
  SET_ACTIVITIES_DETAIL_REPORT(state, { activitiesReport, moreData }) {
    if (moreData) {
      state.activitiesDetailReport = state.activitiesDetailReport.concat(activitiesReport);
    } else {
      state.activitiesDetailReport = activitiesReport;
    }
  },
  CLEAR_ACTIVITIES_DETAIL_REPORT(state) {
    state.activitiesDetailReport = undefined;
  },
  SET_ACTIVITIES_QUEUE_REPORT(state, { activitiesReport }) {
    state.activitiesQueueReport = activitiesReport;
  },
  SET_ALL_REPORTS_ENQUIRED_WITHOUT_EXPORT_FALSE(state) {
    mutations.SET_DETAIL_REPORT_ENQUIRED_WITHOUT_EXPORT(state, false);
    mutations.SET_QUEUE_REPORT_ENQUIRED_WITHOUT_EXPORT(state, false);
    mutations.SET_EMPLOYMENT_REPORT_ENQUIRED_WITHOUT_EXPORT(state, false);
  },
  SET_DETAIL_REPORT_ENQUIRED_WITHOUT_EXPORT(state, val) {
    state.detailReportEnquiredWithoutExport = val;
  },
  CLEAR_ACTIVITIES_QUEUE_REPORT(state) {
    state.activitiesQueueReport = undefined;
  },
  SET_QUEUE_REPORT_ENQUIRED_WITHOUT_EXPORT(state, val) {
    state.queueReportEnquiredWithoutExport = val;
  },
  SET_ACTIVITIES_EMPLOYMENT_REPORT(state, { activitiesReport, moreData }) {
    if (moreData) {
      state.activitiesEmploymentReport.operators = state.activitiesEmploymentReport
        .operators.concat(activitiesReport.operators);
    } else {
      state.activitiesEmploymentReport = activitiesReport;
    }
  },
  CLEAR_ACTIVITIES_EMPLOYMENT_REPORT(state) {
    state.activitiesEmploymentReport = undefined;
  },
  SET_EMPLOYMENT_REPORT_ENQUIRED_WITHOUT_EXPORT(state, val) {
    state.employmentReportEnquiredWithoutExport = val;
  },
  SET_MAXIMUM_SKILL_VALUE(state, value) {
    state.maximumSkillValue = value;
  },
  OPERATIONAL_REPORTS(state, value) {
    state.operationalReports = value;
  },
  SET_ACTIVITIES_REPORT_DATES(state, value) {
    const oldTotalCount = state.activitiesDetailReportDates.totalCount;
    state.activitiesDetailReportDates = value;
    state.activitiesDetailReportDates
      .totalCount = value.totalCount || oldTotalCount;
  },
  CLEAR_ACTIVITIES_REPORT_DATES(state) {
    state.activitiesDetailReportDates = {
      dateFrom: '',
      dateTo: '',
      page: 1,
      totalCount: 0,
      pageSize: undefined,
    };
  },
  SET_ACTIVITIES_EMPLOYMENT_DATES(state, value) {
    const oldTotalCount = state.activitiesEmploymentReportDates.totalCount;
    state.activitiesEmploymentReportDates = value;
    state.activitiesEmploymentReportDates
      .totalCount = value.totalCount || oldTotalCount;
  },
  CLEAR_ACTIVITIES_EMPLOYMENT_DATES(state) {
    state.activitiesEmploymentReportDates = {
      dateFrom: '',
      dateTo: '',
      page: 1,
      totalCount: 0,
      pageSize: undefined,
    };
  },
  SET_ACTIVITIES_QUEUE_DATES(state, value) {
    state.activitiesQueueReportDates = value;
  },
  CLEAR_ACTIVITIES_QUEUE_DATES(state) {
    state.activitiesQueueReportDates = {
      dateFrom: '',
      dateTo: '',
    };
  },
  SET_ACTIVITIES_STATES(state, array) {
    state.activitiesStates = array;
  },
  SET_ACTIVITY_QUEUE(state, activities) {
    state.activityQueue = activities;
  },
  SET_LOADING_REPORT(state) {
    state.loadingReport = true;
  },
  SET_STOP_LOADING_REPORT(state) {
    state.loadingReport = false;
  },
  SET_CSV_REPORT_DATA(state, { // проставляет как статус, так и url
    reportType, teamId, status, url,
  }) {
    const key = String(teamId);
    if (!key || key === 'null') return;
    const camelReportType = convertSnakeToCamel(reportType);
    const camelStatus = convertSnakeToCamel(status);
    if (!state.csvReports[camelReportType] || typeof state.csvReports[camelReportType] !== 'object') {
      Vue.set(state.csvReports, camelReportType, {});
    }
    Vue.set(state.csvReports[camelReportType], key, { status: camelStatus, url });
  },
  SET_CSV_REPORT_STATUS(state, { // проставляет только статус
    reportType, teamId, status,
  }) {
    const key = String(teamId);
    if (!key || key === 'null') return;
    const camelReportType = convertSnakeToCamel(reportType);
    const camelStatus = convertSnakeToCamel(status);
    if (!state.csvReports[camelReportType] || typeof state.csvReports[camelReportType] !== 'object') {
      Vue.set(state.csvReports, camelReportType, {});
    }
    const reportObj = state.csvReports[camelReportType][key];
    if (reportObj) {
      reportObj.status = camelStatus;
    }
  },
};

const getters = {
  notReadyCount(state) {
    let loggedCount;
    let readyCount;
    if (state.operators.length) {
      loggedCount = state.operators.filter((x) => x.logged).length;
      readyCount = state.operators.filter((x) => x.ready).length;
    } else {
      loggedCount = 0;
      readyCount = 0;
    }
    return loggedCount - readyCount;
  },
  activitiesEmploymentReport(state) {
    return state.activitiesEmploymentReport ? state.activitiesEmploymentReport.operators : undefined;
  },
  absenceReasonsEmploymentReport(state) {
    return state.activitiesEmploymentReport
      ? state.activitiesEmploymentReport.absenceReasons : [];
  },
};

const store = {
  namespaced: true,
  state: data,
  actions,
  mutations,
  getters,
};

export default store;
